import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Mic,
  BarChart3,
  CalendarDays,
  Clock,
  Zap,
  Gift,
  LayoutGrid,
  Network,
  ListChecks,
  Lock,
  ArrowRight,
} from 'lucide-react';
import clsx from 'clsx';

// ─── Framer Motion Variants ────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

// ─── Sub-components ────────────────────────────────────────

function AnimatedSection({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function StatPill({
  icon: Icon,
  label,
}: {
  icon: React.ElementType;
  label: string;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="flex items-center gap-2 bg-[#E8F4FC] border border-[#D8EBFE] rounded-full px-4 py-2 text-sm text-[#004AA9]"
    >
      <Icon size={14} className="text-[#0C91C6]" />
      <span>{label}</span>
    </motion.div>
  );
}

function StepCard({
  number,
  icon: Icon,
  title,
  description,
  accentColor,
}: {
  number: string;
  icon: React.ElementType;
  title: string;
  description: string;
  accentColor: string;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="relative flex flex-col gap-4 p-6 rounded-[24px] bg-white border-2 border-[#D8EBFE] shadow-nera hover:border-white transition-colors group"
    >
      <div className="flex items-start justify-between">
        <div
          className={clsx(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            accentColor
          )}
        >
          <Icon size={22} className="text-white" />
        </div>
        <span className="text-5xl font-black text-[#D8EBFE] group-hover:text-[#E8F4FC] transition-colors leading-none">
          {number}
        </span>
      </div>
      <div>
        <h3 className="text-lg font-heading font-semibold text-[#004AA9] mb-1">{title}</h3>
        <p className="text-[#666666] font-body text-sm leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
  locked,
  accentClass,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  locked?: boolean;
  accentClass: string;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="relative flex flex-col gap-3 p-5 rounded-[24px] bg-white border-2 border-[#D8EBFE] shadow-nera hover:border-blue-100 transition-all group overflow-hidden"
    >
      {locked && (
        <div className="absolute top-3 right-3">
          <Lock size={12} className="text-[#999]" />
        </div>
      )}
      <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', accentClass)}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <h4 className="text-base font-heading font-semibold text-[#004AA9] mb-1">{title}</h4>
        <p className="text-[#666666] font-body text-sm leading-relaxed">{description}</p>
      </div>
      {locked && (
        <p className="text-xs text-[#999] mt-auto pt-2 border-t border-[#D8EBFE]">
          Unlock with email after assessment
        </p>
      )}
    </motion.div>
  );
}

// ─── Main Component ────────────────────────────────────────

export default function LandingPage() {
  const navigate = useNavigate();

  const handleStartAssessment = () => navigate('/assessment/general');

  return (
    <div className="min-h-screen bg-white text-[#1A1A2E] overflow-x-hidden">
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="relative flex flex-col items-center justify-center px-4 py-16 overflow-hidden bg-white">
        {/* Hero Frame — matches main website .hero-frame */}
        <div
          className="relative w-full max-w-[1200px] mx-auto overflow-hidden"
          style={{
            borderRadius: '34px',
            border: '2px solid #FFF',
            background: 'linear-gradient(270deg, #D8EBFE 0%, #ECF6FF 100%)',
            boxShadow: '0 4px 29.8px 0 rgba(0, 74, 169, 0.06)',
            padding: '5rem 2rem',
          }}
        >
          {/* Network decoration top-left */}
          <svg className="absolute top-0 left-0 w-[280px] h-[200px] pointer-events-none" viewBox="0 0 280 200" fill="none">
            <line x1="0" y1="30" x2="30" y2="30" stroke="white" strokeWidth="2"/>
            <line x1="30" y1="30" x2="50" y2="50" stroke="white" strokeWidth="2"/>
            <line x1="50" y1="50" x2="50" y2="80" stroke="white" strokeWidth="2"/>
            <line x1="50" y1="80" x2="30" y2="100" stroke="white" strokeWidth="2"/>
            <line x1="30" y1="100" x2="30" y2="130" stroke="white" strokeWidth="2"/>
            <line x1="30" y1="130" x2="50" y2="150" stroke="white" strokeWidth="2"/>
            <line x1="50" y1="80" x2="80" y2="80" stroke="white" strokeWidth="2"/>
            <line x1="80" y1="80" x2="100" y2="60" stroke="white" strokeWidth="2"/>
            <line x1="100" y1="60" x2="130" y2="60" stroke="white" strokeWidth="2"/>
            <line x1="130" y1="60" x2="150" y2="80" stroke="white" strokeWidth="2"/>
            <line x1="150" y1="80" x2="180" y2="80" stroke="white" strokeWidth="2"/>
            <line x1="180" y1="80" x2="200" y2="60" stroke="#0C91C6" strokeWidth="2"/>
            <line x1="200" y1="60" x2="230" y2="60" stroke="#0C91C6" strokeWidth="2"/>
            <line x1="230" y1="60" x2="250" y2="40" stroke="#0C91C6" strokeWidth="2"/>
            <circle cx="0" cy="30" r="5" fill="white"/>
            <circle cx="30" cy="30" r="4" fill="white"/>
            <circle cx="50" cy="50" r="5" fill="white"/>
            <circle cx="50" cy="80" r="6" fill="white"/>
            <circle cx="30" cy="100" r="4" fill="white"/>
            <circle cx="30" cy="130" r="5" fill="white"/>
            <circle cx="50" cy="150" r="4" fill="white"/>
            <circle cx="80" cy="80" r="4" fill="white"/>
            <circle cx="100" cy="60" r="5" fill="white"/>
            <circle cx="130" cy="60" r="4" fill="white"/>
            <circle cx="150" cy="80" r="5" fill="white"/>
            <circle cx="180" cy="80" r="5" fill="white"/>
            <circle cx="200" cy="60" r="4" fill="#0C91C6"/>
            <circle cx="230" cy="60" r="5" fill="#0C91C6"/>
            <circle cx="250" cy="40" r="6" fill="#0C91C6"/>
          </svg>

          {/* Network decoration bottom-right */}
          <svg className="absolute bottom-0 right-0 w-[280px] h-[200px] pointer-events-none" viewBox="0 0 280 200" fill="none">
            <line x1="30" y1="160" x2="50" y2="140" stroke="#0C91C6" strokeWidth="2"/>
            <line x1="50" y1="140" x2="80" y2="140" stroke="#0C91C6" strokeWidth="2"/>
            <line x1="80" y1="140" x2="100" y2="120" stroke="#0C91C6" strokeWidth="2"/>
            <line x1="100" y1="120" x2="130" y2="120" stroke="white" strokeWidth="2"/>
            <line x1="130" y1="120" x2="150" y2="140" stroke="white" strokeWidth="2"/>
            <line x1="150" y1="140" x2="180" y2="140" stroke="white" strokeWidth="2"/>
            <line x1="180" y1="140" x2="200" y2="120" stroke="white" strokeWidth="2"/>
            <line x1="200" y1="120" x2="230" y2="120" stroke="white" strokeWidth="2"/>
            <line x1="230" y1="120" x2="250" y2="100" stroke="white" strokeWidth="2"/>
            <line x1="250" y1="100" x2="250" y2="70" stroke="white" strokeWidth="2"/>
            <line x1="250" y1="70" x2="280" y2="70" stroke="white" strokeWidth="2"/>
            <line x1="230" y1="120" x2="250" y2="140" stroke="white" strokeWidth="2"/>
            <line x1="250" y1="140" x2="250" y2="170" stroke="white" strokeWidth="2"/>
            <line x1="250" y1="170" x2="280" y2="170" stroke="white" strokeWidth="2"/>
            <circle cx="30" cy="160" r="6" fill="#0C91C6"/>
            <circle cx="50" cy="140" r="5" fill="#0C91C6"/>
            <circle cx="80" cy="140" r="4" fill="#0C91C6"/>
            <circle cx="100" cy="120" r="5" fill="white"/>
            <circle cx="130" cy="120" r="4" fill="white"/>
            <circle cx="150" cy="140" r="5" fill="white"/>
            <circle cx="180" cy="140" r="4" fill="white"/>
            <circle cx="200" cy="120" r="5" fill="white"/>
            <circle cx="230" cy="120" r="6" fill="white"/>
            <circle cx="250" cy="100" r="4" fill="white"/>
            <circle cx="250" cy="70" r="5" fill="white"/>
            <circle cx="280" cy="70" r="5" fill="white"/>
            <circle cx="250" cy="140" r="4" fill="white"/>
            <circle cx="250" cy="170" r="5" fill="white"/>
            <circle cx="280" cy="170" r="5" fill="white"/>
          </svg>

          {/* Content — centered like main website hero */}
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[800px] mx-auto gap-6 py-8">
            {/* Subtitle */}
            <motion.span
              variants={fadeUp}
              className="inline-block text-[#0C91C6] text-2xl font-semibold leading-[120%]"
              style={{ fontFamily: '"Work Sans", sans-serif' }}
            >
              AI Readiness Assessment
            </motion.span>

            {/* Title */}
            <motion.h1
              variants={fadeUp}
              className="text-[44px] font-semibold leading-[120%] text-[#004AA9]"
              style={{ fontFamily: '"Work Sans", sans-serif' }}
            >
              Is Sensitive Data Blocking<br />Your AI Strategy?
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={fadeUp}
              className="text-base text-[#666] max-w-[600px] mx-auto leading-[1.8]"
            >
              Have a 10-minute conversation with our AI consultant and instantly discover where privacy
              and compliance constraints are holding back your team, and exactly how to fix it.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center gap-4 mt-8">
              <button
                onClick={handleStartAssessment}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold text-white rounded-full cursor-pointer transition-all duration-300 border-none bg-[#0B3D91] hover:bg-[#0a3377] hover:-translate-y-0.5 hover:shadow-md"
              >
                Start AI Readiness Assessment
                <ArrowRight size={18} />
              </button>
              <a
                href={`${import.meta.env.VITE_MAIN_SITE_URL || 'https://nera.systems'}/contact`}
                className="inline-flex items-center justify-center px-8 py-3.5 text-sm font-semibold rounded-full cursor-pointer transition-all duration-300 border-2 border-[#0C91C6] text-white bg-[#0C91C6] hover:-translate-y-0.5 hover:shadow-md"
              >
                Contact Us
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div variants={stagger} className="flex flex-wrap justify-center gap-3 mt-4">
              <StatPill icon={Clock} label="10 min conversation" />
              <StatPill icon={Zap} label="Instant analysis" />
              <StatPill icon={Gift} label="100% free" />
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── The Problem ───────────────────────────────── */}
      <section className="py-20 px-4 bg-[#E8F4FC]">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <motion.div variants={fadeIn} className="inline-block mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-red-500">
                The Problem
              </span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-heading font-bold text-[#004AA9] mb-4">
              AI is moving fast. Your most valuable data is stuck behind <span className="text-red-500">"no."</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#666666] font-body text-lg max-w-2xl mx-auto">
              Security and compliance teams are blocking the AI use cases that matter most, because today's tools force an impossible choice.
            </motion.p>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <motion.div
              variants={fadeUp}
              className="p-6 rounded-[24px] border-2 border-red-200 bg-white shadow-nera"
            >
              <div className="text-red-500 text-xs font-bold uppercase tracking-widest mb-3">Option 1: Shadow AI</div>
              <h3 className="text-[#1A1A2E] text-xl font-heading font-bold mb-2">Employees paste sensitive data into ChatGPT anyway</h3>
              <p className="text-[#666666] font-body text-sm leading-relaxed">Customer records, financial data, contracts, all going into public AI models your security team explicitly banned. It's already happening.</p>
              <div className="mt-4 inline-block px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">
                Result: High Risk &amp; Compliance Failure
              </div>
            </motion.div>
            <motion.div
              variants={fadeUp}
              className="p-6 rounded-[24px] border-2 border-[#D8EBFE] bg-white shadow-nera"
            >
              <div className="text-[#0C91C6] text-xs font-bold uppercase tracking-widest mb-3">Option 2: Stagnation</div>
              <h3 className="text-[#1A1A2E] text-xl font-heading font-bold mb-2">AI is only used on trivial, "safe" data</h3>
              <p className="text-[#666666] font-body text-sm leading-relaxed">The data that would actually move the needle (financials, PHI, customer behavior, internal forecasts) stays permanently locked out of AI workflows.</p>
              <div className="mt-4 inline-block px-3 py-1.5 rounded-lg bg-[#E8F4FC] border border-[#D8EBFE] text-[#004AA9] text-xs font-semibold">
                Result: High Opportunity Cost &amp; Competitive Disadvantage
              </div>
            </motion.div>
          </AnimatedSection>

          {/* Stats bar */}
          <AnimatedSection className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl bg-white border border-[#D8EBFE] shadow-nera">
              <div className="text-3xl font-black text-red-500 shrink-0">40%</div>
              <p className="text-[#666666] font-body text-sm leading-relaxed">of AI data breaches will arise from cross-border GenAI misuse by 2027 <span className="text-[#999]">Source: Gartner</span></p>
            </motion.div>
            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl bg-white border border-[#D8EBFE] shadow-nera">
              <div className="text-3xl font-black text-[#0C91C6] shrink-0">62%</div>
              <p className="text-[#666666] font-body text-sm leading-relaxed">of executives say data privacy concerns constrain their organization's ability to adopt AI <span className="text-[#999]">Source: EY</span></p>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────── */}
      <section id="how-it-works" className="py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <motion.div variants={fadeIn} className="inline-block mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#0C91C6]">
                The Process
              </span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-heading font-bold text-[#004AA9] mb-4">
              How It Works
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#666666] font-body text-lg max-w-xl mx-auto">
              Three steps from conversation to a personalized Secure AI Readiness report.
            </motion.p>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <StepCard
              number="01"
              icon={Mic}
              title="Tell Us Your Situation"
              description="Talk with our AI consultant for 10 minutes. It asks targeted questions about your data, AI ambitions, compliance constraints, and current blockers."
              accentColor="bg-[#0B3D91]"
            />
            <StepCard
              number="02"
              icon={BarChart3}
              title="Get Your Readiness Score"
              description="Our AI benchmarks your organization across five privacy and AI readiness dimensions, pinpointing exactly where sensitive data is creating risk or lost value."
              accentColor="bg-[#004AA9]"
            />
            <StepCard
              number="03"
              icon={CalendarDays}
              title="Book a Strategy Session"
              description="Receive your full report with prioritized recommendations, then book a free call with a Nera Solutions Engineer to map out your path forward."
              accentColor="bg-[#0C91C6]"
            />
          </AnimatedSection>

          {/* Connector lines (desktop) */}
          <div className="hidden md:flex items-center justify-center -mt-16 mb-8 px-24 pointer-events-none">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D8EBFE] to-transparent" />
          </div>
        </div>
      </section>

      {/* ── What You'll Get ───────────────────────────── */}
      <section className="py-24 px-4 bg-[#F5FAFD]">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <motion.div variants={fadeIn} className="inline-block mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#0C91C6]">
                Your Report
              </span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-heading font-bold text-[#004AA9] mb-4">
              What's Inside Your Report
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#666666] font-body text-lg max-w-xl mx-auto">
              A detailed breakdown of your privacy posture, AI gaps, and the specific steps to unlock your sensitive data.
            </motion.p>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
              icon={BarChart3}
              title="AI Readiness Scorecard"
              description="Scored across 5 dimensions: AI readiness, data privacy maturity, compliance posture, collaboration capability, and data monetization potential."
              accentClass="bg-[#0B3D91]"
            />
            <FeatureCard
              icon={Network}
              title="Data Flow Diagram"
              description="A visual map of where sensitive data flows in your organization, with compliance choke points and AI blockers highlighted."
              accentClass="bg-[#004AA9]"
            />
            <FeatureCard
              icon={LayoutGrid}
              title="Privacy Tech Landscape"
              description="Your current data and AI tool stack mapped, including gaps where Privacy Enhancing Technologies (PETs) would unlock new capability."
              accentClass="bg-[#0C91C6]"
            />
            <FeatureCard
              icon={ListChecks}
              title="Recommendations + Next Steps"
              description="Prioritized, specific steps to safely unlock AI on your sensitive data, including whether Nera ChatApp or the Nera Confidential AI Platform applies to your situation."
              accentClass="bg-[#00A3E0]"
              locked
            />
          </AnimatedSection>
        </div>
      </section>

      {/* ── Social Proof ──────────────────────────────── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote:
                  '"Nobody is doing what Nera Systems is doing. The gap between enterprise AI ambition and compliance reality is wide open, and they own it."',
                name: 'Senior Director',
                title: 'Google Cloud',
              },
              {
                quote:
                  '"We had sensitive retail data sitting completely untouched because of compliance constraints. Nera let us run analytics on it for the first time, without any data leaving our environment."',
                name: 'Analytics Lead',
                title: 'Regional Transportation Provider',
              },
              {
                quote:
                  '"The assessment identified three data collaboration use cases we had dismissed as impossible. The roadmap gave us a clear path to monetizing data we thought was permanently locked."',
                name: 'Chief Data Officer',
                title: 'Consumer Products Company',
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="p-6 rounded-[34px] bg-gradient-to-l from-[#D8EBFE] to-[#ECF6FF] border-2 border-white shadow-nera"
              >
                <p className="text-[#333333] font-body text-sm leading-relaxed italic mb-4">
                  {testimonial.quote}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0B3D91] to-[#0C91C6] flex items-center justify-center text-xs font-bold text-white">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <p className="text-[#1A1A2E] text-sm font-heading font-semibold">{testimonial.name}</p>
                    <p className="text-[#999] text-xs">{testimonial.title}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────── */}
      <section className="py-24 px-4 bg-[#F5FAFD]">
        <div className="max-w-3xl mx-auto">
          <div
            className="nera-card-dark relative rounded-3xl p-12 text-center overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0B3D91 0%, #004AA9 50%, #0B3D91 100%)',
              border: '1px solid rgba(0, 74, 169, 0.4)',
            }}
          >
            {/* Decorative blobs inside CTA */}
            <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full opacity-20 -translate-y-1/2"
              style={{ background: 'radial-gradient(circle, #00A3E0, transparent)' }} />
            <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full opacity-15 translate-y-1/2"
              style={{ background: 'radial-gradient(circle, #0C91C6, transparent)' }} />

            <AnimatedSection className="relative z-10">
              <motion.div variants={fadeIn} className="inline-block mb-4">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs text-blue-200">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00A3E0] opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#00A3E0]" />
                  </span>
                  No signup required
                </div>
              </motion.div>

              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
                Find out what your sensitive data is really worth.
              </motion.h2>

              <motion.p variants={fadeUp} className="text-blue-200 font-body mb-8 text-lg">
                Get a personalized Secure AI Readiness Report in under 10 minutes, and a clear path to unlocking the AI use cases your compliance team said were impossible.
              </motion.p>

              <motion.div variants={fadeUp}>
                <button
                  onClick={handleStartAssessment}
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-[#0B3D91] text-lg bg-white hover:bg-[#F5FAFD] transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
                  style={{
                    boxShadow: '0 0 30px rgba(255, 255, 255, 0.3)',
                  }}
                >
                  Start Your Free Assessment
                  <ArrowRight
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </motion.div>

              <motion.p variants={fadeUp} className="text-blue-300 text-sm mt-4">
                Takes 10 minutes · No credit card · Instant results
              </motion.p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────── */}
    </div>
  );
}
